<template>
  <expandable-list :hide-borders="!previewMode" start-open>
    <template v-slot:header>
      <div class="products-group-container" :class="bgAndBorderClasses">
        <div class="product-group-content">
          <div class="group-information">
            <div v-if="isMobile" class="blue-chip mr-2">Grupo de produtos</div>

            <div class="img-container">
              <img v-if="imageExists" :src="imageSource" alt="imagem do grupo" width="100%" />
              <mf-icon v-else size="36">image_not_supported</mf-icon>
            </div>
            <div class="right-container">
              <div v-if="!isMobile" class="blue-chip mb-2">Grupo de produtos</div>
              <span class="group-name">{{ groupName }}</span>
              <component :is="previewComponent" v-if="isMobile" class="left-container" :offer="offerSale" :preview-mode="previewMode" />

              <div class="sku-ean-container">
                <span class="promotion-text">
                  <strong>{{ promotionText.bold }}</strong>
                  {{ promotionText.normal }}
                </span>
                <div>
                  <span class="cinza-0-14px mr-2"><strong>Produtos: </strong>{{ productsLength }}</span>
                  <span v-if="isMobile && offerSale.minimum_per_client" class="cinza-0-14px mr-2">
                    <strong>Mín. CPF: </strong>
                    {{ offerSale.minimum_per_client }}
                  </span>
                  <span v-if="isMobile && offerSale.maximum_per_client" class="cinza-0-14px">
                    <strong>Máx. CPF: </strong>
                    {{ offerSale.maximum_per_client }}
                  </span>
                  <span v-if="isMobile && isCashback && offerSale.max_coupons" class="cinza-0-14px">
                    <strong>Máx. ativação por CPF: </strong>{{ offerSale.max_coupons }}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <component :is="previewComponent" v-if="!isMobile" class="left-container" :offer="offerSale" :preview-mode="previewMode" />
        </div>
        <div v-if="previewMode" class="see-more" @click="$emit('goToDetails')">
          <span>Ver mais detalhes</span>
        </div>
      </div>
    </template>
    <template v-slot:content>
      <offer-sales-digital-voucher v-if="isVoucherDigital" :offer-sales="filteredProducts" is-inside-products-group />
      <offer-sales-common v-else :offer-sales="filteredProducts" is-inside-products-group :type="type" :external-synchronization="externalSynchronization" />
    </template>
  </expandable-list>
</template>

<script>
import { localeString } from '@/config/helpers.js'
import ExpandableList from '../ExpandableList.vue'
import OfferSalesCommon from './OfferSalesCommon.vue'
import AbsoluteDiscountMechanic from '../products-mechanics/AbsoluteDiscountMechanic.vue'
import PercentageDiscountMechanic from '../products-mechanics/PercentageDiscountMechanic.vue'
import TakePayMechanic from '../products-mechanics/TakePayMechanic.vue'
import CashbackAbsoluteMechanic from '../products-mechanics/CashbackAbsoluteMechanic.vue'
import VoucherDigitalMechanic from '../products-mechanics/VoucherDigitalMechanic.vue'
import OfferSalesDigitalVoucher from './OfferSalesDigitalVoucher.vue'
import { normalizeText } from '@/modules/industria/helpers/searchHelper.js'

export default {
  components: {
    ExpandableList,
    OfferSalesCommon,
    TakePayMechanic,
    AbsoluteDiscountMechanic,
    PercentageDiscountMechanic,
    CashbackAbsoluteMechanic,
    OfferSalesDigitalVoucher,
  },
  props: {
    offerSale: {
      type: Object,
      required: true,
    },
    type: {
      type: String,
      required: true,
      validator: (value) =>
        [
          'takepay',
          'takepay_offer_sales',
          'general',
          'offer_sales',
          'percentage',
          'percentage_offer_sales',
          'next_buy_cashback',
          'next_buy_cashback_offer_sales',
          'cashback_absolute',
          'cashback_absolute_offer_sales',
          'dynamic_rule_offer_sales',
        ].includes(value),
    },
    previewMode: {
      type: Boolean,
      default: false,
    },
    search: {
      type: String,
      default: '',
    },
    externalSynchronization: {
      type: Object,
      default: () => null,
    },
  },
  computed: {
    products() {
      return this.isVoucherDigital ? this.offerSale?.trigger_products : this.offerSale?.products
    },
    filteredProducts() {
      return this.products.filter((product) => {
        const searched = normalizeText(this.search)
        return (
          normalizeText(product.name).includes(searched) || product.sku.includes(searched) || product.ean.includes(searched) || product.eans.includes(searched)
        )
      })
    },
    previewComponent() {
      switch (this.type) {
        case 'takepay':
        case 'takepay_offer_sales':
          return TakePayMechanic
        case 'general':
        case 'offer_sales':
          return AbsoluteDiscountMechanic
        case 'percentage':
        case 'percentage_offer_sales':
          return PercentageDiscountMechanic
        case 'next_buy_cashback':
        case 'next_buy_cashback_offer_sales':
        case 'cashback_absolute':
        case 'cashback_absolute_offer_sales':
          return CashbackAbsoluteMechanic
        case 'dynamic_rule_offer_sales':
          return VoucherDigitalMechanic
        default:
          return null
      }
    },
    isCashback() {
      return (
        this.type === 'next_buy_cashback' ||
        this.type === 'cashback_absolute' ||
        this.type === 'cashback_absolute_offer_sales' ||
        this.type === 'next_buy_cashback_offer_sales'
      )
    },
    isVoucherDigital() {
      return this.type === 'dynamic_rule_offer_sales'
    },
    bgAndBorderClasses() {
      return this.previewMode ? 'bg-cinza-l90 preview-mode-border' : 'bg-white'
    },

    promotionText() {
      const text = {
        bold: 'Reembolso:',
        normal: '',
      }
      switch (this.type) {
        case 'takepay':
        case 'takepay_offer_sales':
          const diff = this.offerSale?.take - this.offerSale?.pay
          text.normal = diff === 1 ? `${diff} unidade por oferta` : `${diff} unidades por oferta`
          break
        case 'general':
        case 'offer_sales':
          text.normal = `R$ ${localeString(this.offerSale?.rebate, 2)}`
          break
        case 'percentage':
        case 'percentage_offer_sales':
          text.normal = `Todo desconto concedido`
          break
        default:
          break
      }

      if (this.isCashback) {
        text.bold = 'Cashback (pago pela indústria): '
        text.normal = `R$ ${localeString(this.offerSale?.cashback_absolute_discount || this.offerSale?.cashback_absolute, 2)}`
      } else if (this.isVoucherDigital) {
        text.bold = 'Voucher (pago pela indústria): '
        text.normal = `R$ ${localeString(this.offerSale?.benefits?.value, 2)}`
      }
      return text
    },

    imageExists() {
      if (this.isVoucherDigital) {
        return this.offerSale?.trigger_products[0]?.image_url && this.offerSale?.trigger_products[0]?.image_url?.length > 0
      } else {
        return this.offerSale?.group_image_url && this.offerSale?.group_image_url?.length > 0
      }
    },

    imageSource() {
      if (this.isVoucherDigital) {
        return this.offerSale?.trigger_products[0]?.image_url
      } else {
        return this.offerSale?.group_image_url
      }
    },

    groupName() {
      if (this.isVoucherDigital) {
        return this.offerSale?.trigger_products[0]?.group_name
      }
      return this.offerSale?.name
    },

    productsLength() {
      if (this.isVoucherDigital) {
        return this.offerSale?.trigger_products?.length
      }
      return this.products.length
    },
  },
}
</script>

<style lang="scss" scoped>
.products-group-container {
  border-radius: 8px;
  border-top-width: 0;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
}

.preview-mode-border {
  border: 1px solid $cinza-l70;
}

.bg-cinza-l90 {
  background: $cinza-l90;
}

.bg-white {
  background: white;
}

.product-group-content {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;

  @media screen and (max-width: $xs-breakpoint) {
    flex-direction: column;
    gap: 8px;
  }
}

.group-information {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;

  @media screen and (max-width: $xs-breakpoint) {
    flex-direction: column;
    gap: 8px;
  }

  .blue-chip {
    padding: 4px 16px;
    border-radius: 16px;
    font-size: 14px;
    font-weight: bold;
    background: #d2e9ff80;
    color: $marca-0-l10;
    white-space: nowrap;
    width: fit-content;
    user-select: none;
  }

  .group-name {
    font-size: 1rem;
    font-weight: bold;
    color: $cinza-d10;

    @media screen and (max-width: $xs-breakpoint) {
      text-align: center;
    }
  }

  .promotion-text {
    font-size: 14px;
    color: $verde-d20;
  }

  .cinza-0-14px {
    font-size: 14px;
    color: $cinza-0;
  }

  .right-container {
    display: flex;
    flex-direction: column;
    gap: 16px;

    .sku-ean-container {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 22px;

      @media screen and (max-width: $xs-breakpoint) {
        flex-direction: column;
        gap: 8px;
      }
    }
  }
}

.img-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 74px;
  height: 80px;

  & img {
    display: block;
    max-width: 100%;
    max-height: 100%;
    mix-blend-mode: darken;
    object-fit: contain;
  }
}

.see-more {
  background: #d2e9ff99;
  color: $marca-0-l10;
  height: 54px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    cursor: pointer;
    background-color: #b4dbff99;
  }
}
</style>
